import './datasourcesRisky.less'
import {
  ACTION_DATA_SOURCES_RISKY_WIDGET,
  DataSourceRiskyWidget,
  fetchDataSourcesRiskyWidget,
  resetRiskyWidget
} from '../../../dataSources/dataSourcesSlice'
import EmptyStateSvg from '../../../../assets/inlineSvg/no_data_circle.svg'
import useUrlContext from '../../../../hooks/useUrlContext'
import { changeUrl, updateSearchStr } from '../../../../utils/urlUtil'
import { DATA_SOURCE_TYPE, IS_AT_RISK, URL_DATA_SOURCES } from '../../../../constants'
import ChartPie from '../../../../components/ChartPie/chartPie'
import LoaderView from '../../../../components/Loader/loader'
import { RootState } from '../../../../rootReducer'
import { getShowLoader } from '../../../../reducers/requestReducer'
import { ChartColors } from '../constants'
import ChartLabel from '../components/chartLabel'
import { useThemeContext } from '../components/ThemeContext'
import { getDataSourceImage } from '../../../../utils/dataSourceUtil'
import { mockDataSourcesRiskyWidget } from '../../renderer/utils/widgetMockdata'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import React, { useEffect } from 'react'
import { Typography, Tooltip } from '@lightbeamai/design-system'

interface OwnProps {
  useMockData?: boolean
}

interface StateProps {
  widget?: DataSourceRiskyWidget
  showLoader: boolean
}

interface DispatchProps {
  fetchDataSourcesRiskyWidget: () => void
  resetRiskyWidget: () => void
}

type DataSourcesRiskyComponentProps = StateProps & DispatchProps & OwnProps

const DataSourcesRiskyWidget = (props: DataSourcesRiskyComponentProps): React.ReactElement => {
  const { widget, showLoader, useMockData, fetchDataSourcesRiskyWidget, resetRiskyWidget } = props
  const { total = 0, risky = 0, datasources } = widget || {}
  const toShowDatasources = datasources?.slice(0, 6)

  const intl = useIntl()
  const textHealthy = intl.formatMessage({ id: 'dataSources.widget.risk.healthy' })
  const textAtRisk = intl.formatMessage({ id: 'dataSources.widget.risk.risky' })
  const context = useUrlContext()
  useEffect(() => {
    if (useMockData) return
    fetchDataSourcesRiskyWidget()
    return () => resetRiskyWidget()
  }, [fetchDataSourcesRiskyWidget, resetRiskyWidget, useMockData])

  const handleGoToRisky = () => {
    changeUrl(URL_DATA_SOURCES, {
      [URL_DATA_SOURCES]: `${IS_AT_RISK}:true`
    })
  }
  const handleGoToAll = () => {
    changeUrl(URL_DATA_SOURCES)
  }

  const handleGoToDatasource = ({ id, type }) => {
    changeUrl(URL_DATA_SOURCES + `/${id}`, {
      [DATA_SOURCE_TYPE]: type
    })
  }

  const { theme } = useThemeContext()

  const chartData = [
    {
      key: textAtRisk,
      value: risky || 0,
      color: theme === 'light-theme' ? ChartColors.Orange : '#FF732C'
    },
    {
      key: textHealthy,
      value: total - risky,
      color: theme === 'light-theme' ? ChartColors.Purple : '#4E74FF'
    }
  ]

  return (
    <div className="lb-widget-card risk risk-summary xs-p-16">
      <div
        className="datasources-risky-widget"
        data-test-id="datasources-data-sources-risky-widget"
      >
        <Typography color="primary" variant="h6">
          {intl.formatMessage({ id: 'dataSources.widget.risk.title' })}
        </Typography>
        {widget && widget?.total > 0 ? (
          <ChartPie
            data={chartData}
            link={[
              {
                key: textAtRisk,
                link: `${URL_DATA_SOURCES}?${updateSearchStr(context, URL_DATA_SOURCES, {
                  [URL_DATA_SOURCES]: `${IS_AT_RISK}: true`
                })}`
              },
              {
                key: textHealthy,
                link: URL_DATA_SOURCES
              }
            ]}
            total={widget.total}
            width={200}
            gap={2}
            showHalfCircle
            centerCountStyles={{
              fontSize: '1.714rem',
              fontWeight: 700,
              y: 85
            }}
            className="pie-chart"
            totalLink={URL_DATA_SOURCES}
          />
        ) : (
          <div className="empty-state-icon">
            <EmptyStateSvg />
            <Typography className="xs-mt-12" variant="baseMinus2" type="medium" color="placeholder">
              {intl.formatMessage({ id: 'dashboard.widget.noData' })}
            </Typography>
          </div>
        )}
        {widget && widget?.total > 0 && (
          <>
            <div className="legend pointer">
              <ChartLabel
                onClick={handleGoToAll}
                text={textHealthy}
                count={total - risky}
                lineColor={ChartColors.Purple}
              />
              <ChartLabel
                onClick={handleGoToRisky}
                text={textAtRisk}
                count={risky}
                lineColor={ChartColors.Orange}
              />
            </div>
            {!!toShowDatasources?.length && !!datasources?.length && (
              <div className="datasources-list-legend">
                <Typography
                  className="xs-mt-0 xs-mb-12"
                  variant="baseMinus2"
                  type="medium"
                  color="primary"
                >
                  {intl.formatMessage({ id: 'dashboard.widget.risky' })}
                </Typography>
                <div className="datasource-items">
                  {toShowDatasources.map((ds) => (
                    <div
                      key={ds.id}
                      className="datasource-legend-item"
                      onClick={handleGoToDatasource.bind(null, { id: ds.id, type: ds.type })}
                    >
                      <Tooltip placement="bottom" title={ds.name} trigger="hover">
                        <img
                          src={getDataSourceImage(
                            ds.type,
                            '../../../assets/images/dataSourceTypes/'
                          )}
                          className="ds-image"
                          onError={({ currentTarget }) => {
                            currentTarget.onerror = null // prevents looping
                            currentTarget.src = getDataSourceImage(
                              ds.type,
                              '../../../assets/images/dataSourceTypes/',
                              true
                            )
                          }}
                        />
                      </Tooltip>
                    </div>
                  ))}
                  {datasources.length - toShowDatasources.length > 0 && (
                    <div className="datasource-legend-item">
                      <Tooltip
                        placement="bottom"
                        title={datasources
                          .slice(6)
                          .map((ds) => ds.name)
                          .join(', ')}
                        trigger="hover"
                      >
                        <Typography
                          className="xs-mt-0 xs-mb-12"
                          variant="baseMinus2"
                          type="medium"
                          color="primary"
                        >
                          & {datasources.length - toShowDatasources.length}{' '}
                          {intl.formatMessage({ id: 'dashboard.widget.more' })}
                        </Typography>
                      </Tooltip>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
        <LoaderView showLoader={showLoader} />
      </div>
    </div>
  )
}

const mapStateToProps = (state: RootState, ownProps: OwnProps): StateProps => ({
  widget: ownProps?.useMockData
    ? (mockDataSourcesRiskyWidget as DataSourceRiskyWidget)
    : state.dataSources.riskWidget,

  showLoader: getShowLoader(ACTION_DATA_SOURCES_RISKY_WIDGET)
})

const mapDispatchToProps: DispatchProps = { fetchDataSourcesRiskyWidget, resetRiskyWidget }

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  mapStateToProps,
  mapDispatchToProps
)(DataSourcesRiskyWidget)
